// Kopfzeile der Chart-Karte — die einzige Ablesefläche.
//
// Im Ruhezustand zeigt sie das nächste Meeting, beim Fahren über den Chart den
// Punkt unter dem Cursor. Die Felder bleiben dieselben, nur die Werte wechseln.
// Dadurch gibt es keine zweite Stelle, an der Zahlen auftauchen.
//
// Bewusst ein Grid mit festen Spaltenbreiten und keine Flex-Reihe: die Labels
// wechseln beim Hovern ("Anchor heute" wird zu "Anchor", "Nächstes Meeting" zu
// "Meeting am"), und in einer Flex-Reihe verschiebt jede Breitenänderung alle
// folgenden Spalten. Feste Spalten heißen, dass nichts wandert — egal welcher
// Text gerade drinsteht.
//
// Aus demselben Grund hat die Wertzeile eine feste Höhe: die große PIPR-Zahl,
// die Entscheidung und die kleinen Zahlen sind unterschiedlich groß, sollen aber
// auf derselben Grundlinie sitzen, damit die Unterzeilen fluchten.
//
// Reihenfolge von links: PIPR, dann die beiden Größen, aus denen sie besteht,
// dann die Entscheidung und ihr Termin.
//
// Die Spalte für den beschlossenen Stand hängt an der Bank, nicht am gerade
// gelesenen Punkt: sobald ein Schritt offen ist, bleibt der Platz reserviert und
// wird leer gezeigt, wo er nichts zu sagen hat. Sonst springt die ganze Zeile,
// je nachdem, worüber der Zeiger steht.
//
// Der Umschalter zwischen Raten- und Änderungsansicht sitzt hier und nicht als
// Seiten-Navigation, weil nur an dieser Stelle zwei wirklich gleichrangige
// Alternativen zur Wahl stehen. Der Währungsumschalter steht direkt hinter dem
// Titel: er wählt den Datensatz, nicht die Sicht darauf, und der Titel sagt,
// worauf er sich bezieht.

import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { PALETTE } from "./palette";
import type { Summary, SummaryStat } from "./summary";
import type { ModeKey, RangeKey } from "./chart/model";
import type { Strings } from "./strings";
import type { Bank } from "./types";

interface ChartHeaderProps {
    strings: Strings;
    summary: Summary | null;
    bank: Bank;
    /** true, solange die Bank einen beschlossenen, nicht wirksamen Schritt hat */
    pendingOpen: boolean;
    range: RangeKey;
    mode: ModeKey;
    onBank: (b: Bank) => void;
    onRange: (r: RangeKey) => void;
    onMode: (m: ModeKey) => void;
}

const NBSP = "\u00a0";

/** Höhe der Wertzeile — trägt die größte Schrift, gilt für alle Zellen. */
const VALUE_H = { xs: 36, md: 46 };

const COLUMNS = {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "200px 140px 140px 240px 200px",
};

const COLUMNS_PENDING = {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "200px 140px 140px 140px 240px 200px",
};

const buttonSx = {
    textTransform: "none" as const,
    fontSize: "0.78rem",
    px: 1.2,
    py: 0.4,
    color: PALETTE.inkMuted,
    borderColor: "rgba(245, 245, 245, 0.2)",
    "&.Mui-selected": {
        color: PALETTE.ink,
        backgroundColor: "rgba(245, 245, 245, 0.1)",
    },
    "&.Mui-selected:hover": { backgroundColor: "rgba(245, 245, 245, 0.16)" },
};

const symbolSx = {
    mr: 0.6,
    fontSize: "0.9rem",
    lineHeight: 1,
};

const captionSx = {
    color: PALETTE.inkFaint,
    display: "block",
    // Ein zweizeiliges Label würde die Zelle höher machen als ihre Nachbarn.
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
};

interface CellProps extends SummaryStat {
    size?: "lg" | "md" | "sm";
}

const Cell = ({ label, value, sub, color, size = "md" }: CellProps) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={captionSx}>
            {label}
        </Typography>

        <Box sx={{ height: VALUE_H, display: "flex", alignItems: "flex-end" }}>
            <Typography
                component="div"
                sx={{
                    color: color ?? PALETTE.ink,
                    fontSize:
                        size === "lg"
                            ? { xs: "2.1rem", md: "2.6rem" }
                            : size === "md"
                                ? { xs: "1.25rem", md: "1.45rem" }
                                : "1.05rem",
                    fontWeight: size === "sm" ? 400 : 500,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                }}
            >
                {value}
            </Typography>
        </Box>

        <Typography variant="caption" sx={captionSx}>
            {sub ?? NBSP}
        </Typography>
    </Box>
);

const EmptyCell = () => <Cell label={NBSP} value={NBSP} />;

const DistributionBar = ({ summary }: { summary: Summary }) => (
    <Box sx={{ mt: 2.5 }}>
        <Typography variant="caption" sx={{ color: PALETTE.inkFaint, display: "block", mb: 0.75 }}>
            {summary.binsLabel}
            {"  ·  "}
            {summary.binsNote}
        </Typography>

        <Box sx={{ display: "flex", gap: "2px", height: 10, mb: 1 }}>
            {summary.bins.map((b) => (
                <Box
                    key={b.label}
                    sx={{
                        flex: b.share,
                        minWidth: b.share > 0 ? "2px" : 0,
                        backgroundColor: b.color,
                        opacity: b.isModal ? 1 : 0.65,
                        borderRadius: "2px",
                        // Innenkontur statt Rand: kein Einfluss auf die Breite,
                        // also springt der Balken beim Moduswechsel nicht.
                        boxShadow: b.isModal
                            ? "inset 0 0 0 1px rgba(245, 245, 245, 0.85)"
                            : "none",
                        transition: "flex-grow 120ms linear",
                    }}
                />
            ))}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.5, md: 2.5 } }}>
            {summary.bins.map((b) => (
                <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "2px",
                            backgroundColor: b.color,
                            opacity: b.isModal ? 1 : 0.65,
                            boxShadow: b.isModal
                                ? "inset 0 0 0 1px rgba(245, 245, 245, 0.85)"
                                : "none",
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: b.isModal ? PALETTE.ink : PALETTE.inkMuted,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {b.label} {b.pctLabel}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

export const ChartHeader = ({
    strings: s,
    summary,
    bank,
    pendingOpen,
    range,
    mode,
    onBank,
    onRange,
    onMode,
}: ChartHeaderProps) => (
    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid rgba(245, 245, 245, 0.1)" }}>
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: PALETTE.inkMuted }}>
                        {s.heading}
                    </Typography>
                    <Typography variant="caption" sx={{ color: PALETTE.inkFaint }}>
                        {bank === "ecb" ? s.bankEcb : s.bankFed}
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={bank}
                    aria-label={s.bankAria}
                    onChange={(_, v) => v && onBank(v as Bank)}
                >
                    <ToggleButton value="fed" sx={buttonSx}>
                        <Box component="span" sx={symbolSx}>
                            {s.b_fed_sym}
                        </Box>
                        {s.b_fed}
                    </ToggleButton>
                    <ToggleButton value="ecb" sx={buttonSx}>
                        <Box component="span" sx={symbolSx}>
                            {s.b_ecb_sym}
                        </Box>
                        {s.b_ecb}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={range}
                    onChange={(_, v) => v && onRange(v as RangeKey)}
                >
                    <ToggleButton value="m1" sx={buttonSx}>{s.r_m1}</ToggleButton>
                    <ToggleButton value="m3" sx={buttonSx}>{s.r_m3}</ToggleButton>
                    <ToggleButton value="y1" sx={buttonSx}>{s.r_y1}</ToggleButton>
                    <ToggleButton value="all" sx={buttonSx}>{s.r_all}</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={(_, v) => v && onMode(v as ModeKey)}
                >
                    <ToggleButton value="rate" sx={buttonSx}>{s.m_rate}</ToggleButton>
                    <ToggleButton value="delta" sx={buttonSx}>{s.m_delta}</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Box>

        {summary && (
            <>
                <Box
                    sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: pendingOpen ? COLUMNS_PENDING : COLUMNS,
                        justifyContent: "start",
                        columnGap: { xs: 2, md: 3 },
                        rowGap: 2,
                    }}
                >
                    <Cell
                        label="PIPR"
                        value={summary.headline}
                        sub={summary.headlineCaption}
                        size="lg"
                    />
                    <Cell {...summary.anchor} />
                    {pendingOpen &&
                        (summary.decided ? <Cell {...summary.decided} /> : <EmptyCell />)}
                    <Cell {...summary.priced} />
                    <Cell {...summary.decision} size="md" />
                    <Cell {...summary.meeting} />
                </Box>

                <DistributionBar summary={summary} />

                {/* Feste Höhe, damit der Chart beim Hovern nicht springt. */}
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mt: 1.25,
                        minHeight: 18,
                        color: PALETTE.inkFaint,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {summary.note || NBSP}
                </Typography>
            </>
        )}
    </Box>
);